"use client";

import { LucideIcon } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";

export interface ThreeColumnItem {
  title: string;
  description: string;
  icon: LucideIcon;
  accent: string;
}

export interface ThreeColumnSectionProps {
  id?: string;
  eyebrow: string;
  title: string;
  intro?: string;
  left: { title: string; items: { name: string; description: string }[]; icon: LucideIcon; accent: string };
  right: { title: string; items: { name: string; description: string }[]; icon: LucideIcon; accent: string };
}

export function TwoListSection({
  id = "two-list",
  eyebrow,
  title,
  intro,
  left,
  right,
}: ThreeColumnSectionProps) {
  return (
    <Section id={id} className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny" style={{ color: left.accent }}>
              {eyebrow}
            </span>
            <h2 className="mt-3">{title}</h2>
            {intro && (
              <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{intro}</p>
            )}
          </div>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-2 gap-6">
          {[left, right].map((col, idx) => {
            const Icon = col.icon;
            return (
              <Reveal key={col.title} delay={idx * 100}>
                <div
                  className="glass rounded-2xl p-6 h-full"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${col.accent} 14%, transparent)`,
                  }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span
                      className="grid place-items-center h-10 w-10 rounded-lg"
                      style={{
                        background: `color-mix(in oklch, ${col.accent} 18%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${col.accent} 35%, transparent)`,
                      }}
                    >
                      <Icon className="h-5 w-5" style={{ color: col.accent }} />
                    </span>
                    <div className="eyebrow-tiny" style={{ color: col.accent }}>
                      {col.title}
                    </div>
                  </div>
                  <div className="space-y-4">
                    {col.items.map((item, i) => (
                      <div key={item.name} className="flex items-start gap-3">
                        <span
                          className="grid place-items-center h-7 w-7 rounded-full text-[10px] font-bold shrink-0"
                          style={{
                            background: `color-mix(in oklch, ${col.accent} 18%, transparent)`,
                            color: col.accent,
                          }}
                        >
                          {i + 1}
                        </span>
                        <div>
                          <div className="font-semibold text-sm">{item.name}</div>
                          <div className="text-xs text-[rgb(var(--fg-muted))] mt-0.5 leading-relaxed">{item.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}
